// File: calendar.js
//
// The Calendar page. Shows the Increasing H.O.P.E events calendar
// embedded directly in the app (via an iframe pointing at the
// public events page on increasinghope.org).
//
// The URL of the embedded calendar is in the constant `CALENDAR_URL`
// inside CalendarPage() below. Change that to point at a different
// events source.
//
// Sections in this file:
//   - Icons (small SVGs for buttons)
//   - Helper components: SoftCard (card wrapper), OpenButton (the
//     yellow "Open" pill), EmbedPanel (the iframe panel)
//   - CalendarPage (the page itself: header + embed + side panel)

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "./firebaseConfig";
import { signOut } from "firebase/auth";

import logo from "./assets/512.png";
import { COLORS, THEME, pageBackground } from "./theme";
import LogoutConfirm from "./components/LogoutConfirm";

function IconExternal({ className = "w-4 h-4" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 3h7v7" />
      <path d="M10 14L21 3" />
      <path d="M21 14v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h6" />
    </svg>
  );
}

function IconCalendar({ className = "w-6 h-6" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M8 3v2M16 3v2" />
      <path d="M4 8h16" />
      <path d="M6 5h12a2 2 0 012 2v13a2 2 0 01-2 2H6a2 2 0 01-2-2V7a2 2 0 012-2z" />
    </svg>
  );
}

// A white-ish rounded card used several times on this page. Wraps
// child content with our standard card background, border, and shadow.
function SoftCard({ children, className = "" }) {
  return (
    <div
      className={`rounded-3xl border backdrop-blur ${className}`}
      style={{
        backgroundColor: THEME.cardBg,
        borderColor: THEME.border,
        boxShadow: THEME.shadow,
      }}
    >
      {children}
    </div>
  );
}

function OpenButton({ href, children, fullWidth = false }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className={[
        "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-extrabold border transition hover:-translate-y-0.5 active:scale-[0.99]",
        fullWidth ? "w-full" : "",
      ].join(" ")}
      style={{
        color: COLORS.brown,
        backgroundColor: COLORS.yellowBase,
        borderColor: "rgba(61,51,0,0.2)",
        boxShadow: "0 12px 24px rgba(61,51,0,0.14)",
      }}
    >
      <IconExternal />
      {children}
    </a>
  );
}

// The big card that shows the embedded calendar in an iframe.
// If the iframe doesn't load within ~3.5 seconds, we show a hint
// telling the user to tap "Open" to view it in a new tab instead
// (some browsers/extensions block embedded sites).
function EmbedPanel({ title, url }) {
  const [timedOut, setTimedOut] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    setTimedOut(false);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      setTimedOut(true);
    }, 3500);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [url]);

  const handleLoad = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setTimedOut(false);
  };

  return (
    <SoftCard className="p-4 sm:p-6">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div
            className="text-lg sm:text-xl font-extrabold"
            style={{ color: THEME.text }}
          >
            {title}
          </div>

          <div
            className="text-sm mt-1"
            style={{ color: "rgba(31,56,0,0.62)" }}
          >
            Browse upcoming Increasing H.O.P.E. events without leaving the app.
          </div>
        </div>

        <div className="hidden sm:block shrink-0">
          <OpenButton href={url}>Open</OpenButton>
        </div>
      </div>

      <div className="sm:hidden mt-4">
        <OpenButton href={url} fullWidth>
          Open full-screen
        </OpenButton>
      </div>

      {timedOut ? (
        <div
          className="mt-4 rounded-2xl border p-3 text-[13px]"
          style={{
            backgroundColor: COLORS.greenLight,
            borderColor: THEME.border,
            color: "rgba(31,56,0,0.74)",
          }}
        >
          If this embed stays blank, tap “Open” to view the calendar in a new
          tab.
        </div>
      ) : null}

      <div
        className="mt-4 rounded-2xl border overflow-hidden bg-white"
        style={{
          borderColor: THEME.border,
          boxShadow: "0 12px 26px rgba(31,56,0,0.08)",
        }}
      >
        <iframe
          title={title}
          src={url}
          onLoad={handleLoad}
          className="w-full h-[55vh] sm:h-[58vh] lg:h-[62vh]"
          style={{ border: "0px" }}
          loading="lazy"
          allow="clipboard-read; clipboard-write; fullscreen"
        />
      </div>
    </SoftCard>
  );
}

function CalendarPage() {
  const navigate = useNavigate();

  // The events page we embed. Change this URL to point the calendar
  // at a different events source.
  const CALENDAR_URL = "https://increasinghope.org/events/";

  // Drives the logout confirmation popup.
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  // Signs the user out of Firebase and sends them back to the
  // login screen. Runs after they click "Log out" in the popup.
  const handleLogout = useCallback(async () => {
    try {
      setLoggingOut(true);
      await signOut(auth);
      localStorage.removeItem("userEmail");
      navigate("/", { replace: true });
    } catch (e) {
      console.error("Logout failed:", e);
      alert("Logout failed. Please try again.");
      setLoggingOut(false);
      setLogoutConfirmOpen(false);
    }
  }, [navigate]);

  return (
    <div className="flex-1 relative" style={pageBackground}>
      <header
        className="sticky top-0 z-30 backdrop-blur border-b shadow-[0_6px_18px_rgba(31,56,0,0.16)]"
        style={{
          backgroundColor: THEME.headerBg,
          borderColor: "rgba(231,247,210,0.28)",
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2 sm:py-3 flex items-center justify-between gap-3">
          <button
            onClick={() => navigate("/home")}
            className="flex items-center gap-3 rounded-xl px-2 py-1 hover:bg-white/10 transition"
            aria-label="Go to home"
          >
            <img src={logo} alt="Logo" className="w-13 h-12 object-contain" />

            <div className="leading-tight text-left">
              <div className="text-sm font-extrabold text-white">
                H.O.P.E Navigator
              </div>
              <div
                className="text-[12px] font-semibold"
                style={{ color: COLORS.yellowLight }}
              >
                Calendar
              </div>
            </div>
          </button>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={() => navigate("/home")}
              className="text-sm font-extrabold text-white/90 hover:text-white transition rounded-xl px-3 py-2 hover:bg-white/10"
            >
              Home
            </button>

            <button
              onClick={() => setLogoutConfirmOpen(true)}
              className="text-sm font-extrabold text-white/90 hover:text-white transition rounded-xl px-3 py-2 hover:bg-white/10"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* --- Main two-column layout. Left = page intro card + embedded
              calendar. Right (sidebar) = "Have a question?" card with
              the link to the Inquiry page. On mobile they stack. --- */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6 lg:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
          <section>
            <SoftCard className="p-5 sm:p-6 lg:p-8 relative overflow-hidden">
              <div
                className="absolute inset-x-0 top-0 h-1"
                style={{ backgroundColor: COLORS.yellowBase }}
              />

              <div
                className="inline-flex items-center rounded-full px-3 py-1 text-sm font-extrabold"
                style={{
                  color: COLORS.brown,
                  backgroundColor: COLORS.yellowLight,
                  border: "1px solid rgba(61,51,0,0.14)",
                }}
              >
                Events
              </div>

              <div className="mt-5 flex items-start gap-4">
                <div
                  className="hidden sm:flex rounded-2xl p-3 border bg-white"
                  style={{
                    color: COLORS.aquaDark,
                    borderColor: THEME.border,
                    boxShadow: "0 10px 22px rgba(31,56,0,0.08)",
                  }}
                >
                  <IconCalendar />
                </div>

                <div className="min-w-0">
                  <h1
                    className="text-3xl sm:text-4xl font-extrabold"
                    style={{ color: THEME.text }}
                  >
                    Community Calendar
                  </h1>

                  <p
                    className="mt-2 max-w-2xl"
                    style={{ color: "rgba(31,56,0,0.66)" }}
                  >
                    View upcoming Increasing H.O.P.E. events, programs, and
                    community opportunities.
                  </p>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <OpenButton href={CALENDAR_URL}>Open Events Calendar</OpenButton>

                <button
                  type="button"
                  onClick={() => navigate("/home")}
                  className="inline-flex items-center justify-center rounded-2xl px-4 py-3 text-sm font-extrabold border bg-white transition hover:-translate-y-0.5 active:scale-[0.99]"
                  style={{
                    color: THEME.text,
                    borderColor: THEME.border,
                    boxShadow: "0 12px 24px rgba(31,56,0,0.08)",
                  }}
                >
                  Back to Home
                </button>
              </div>
            </SoftCard>

            <div className="mt-6">
              <EmbedPanel
                title="Increasing H.O.P.E Events Calendar"
                url={CALENDAR_URL}
              />
            </div>
          </section>

          <aside className="hidden lg:block">
            <div className="sticky top-[64px] sm:top-[72px] space-y-4">
              <SoftCard className="p-4 sm:p-5 lg:p-6">
                <div
                  className="text-sm font-extrabold"
                  style={{ color: THEME.text }}
                >
                  Calendar Info
                </div>

                <div
                  className="mt-4 rounded-2xl border p-4"
                  style={{
                    backgroundColor: COLORS.greenLight,
                    borderColor: THEME.border,
                  }}
                >
                  <div
                    className="text-sm font-extrabold"
                    style={{ color: THEME.text }}
                  >
                    Having trouble viewing it?
                  </div>

                  <p
                    className="mt-2 text-sm leading-6"
                    style={{ color: "rgba(31,56,0,0.68)" }}
                  >
                    Some browsers block embedded pages. Opening the calendar in
                    a new tab is the easiest fix.
                  </p>

                  <div className="mt-4">
                    <OpenButton href={CALENDAR_URL} fullWidth>
                      Open Calendar
                    </OpenButton>
                  </div>
                </div>
              </SoftCard>

              <SoftCard className="p-4 sm:p-5 lg:p-6">
                <div
                  className="text-sm font-extrabold"
                  style={{ color: THEME.text }}
                >
                  Need help?
                </div>

                <p
                  className="mt-3 text-sm leading-6"
                  style={{ color: "rgba(31,56,0,0.66)" }}
                >
                  Use the inquiry page if you have a question about an event,
                  registration, or upcoming opportunity.
                </p>

                <button
                  type="button"
                  onClick={() => navigate("/inquiry")}
                  className="mt-4 w-full rounded-2xl px-4 py-3 text-sm font-extrabold text-white transition hover:-translate-y-0.5 active:scale-[0.99]"
                  style={{
                    backgroundColor: COLORS.aquaDark,
                    boxShadow: "0 12px 24px rgba(0,96,110,0.18)",
                  }}
                >
                  Go to Inquiry
                </button>
              </SoftCard>
            </div>
          </aside>
        </div>
      </main>

      <LogoutConfirm
        open={logoutConfirmOpen}
        busy={loggingOut}
        onCancel={() => setLogoutConfirmOpen(false)}
        onConfirm={handleLogout}
      />
    </div>
  );
}

export default CalendarPage;